import { useState, useEffect, useRef } from 'react';
import { useEventCallback } from '../../hooks/use-event-callback';
import OrderItemFilters, { useOrderItemFilters } from '../../components/OrderItemFilters';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ordersAPI, couriersAPI, courierAPI } from '../../lib/api';
import { fetchWithCache } from '../../lib/cache';
import { toast } from 'sonner';
import { Search, Eye, Truck, FileText, Printer, Package, RefreshCcw, Ship, CheckCircle, Clock, ArrowRight, X, Receipt } from 'lucide-react';
import { printInvoice as generatePrintInvoice } from '../../utils/printInvoice';

export default function AdminShipping() {
  const itemFilters = useOrderItemFilters();
  const requestRef = useRef(0);
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusTab, setStatusTab] = useState('pending'); // New status tab state
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [gstFilter, setGstFilter] = useState(searchParams.get('gst_applied') || 'all');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updateData, setUpdateData] = useState({ status: '', tracking_number: '', courier_provider: '', notes: '' });
  const [shippingOrder, setShippingOrder] = useState(false);
  const [printSize, setPrintSize] = useState('a4'); // 'a4' or 'thermal'
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printOrder, setPrintOrder] = useState(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(20);

  const fetchData = useEventCallback(async (pageNum = page) => {
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        limit,
        search: search || undefined,
        source: 'online', // Only online customer orders
        status: statusTab // Filter by selected status tab
      };
      if (paymentFilter !== 'all') params.payment_method = paymentFilter;
      if (gstFilter !== 'all') params.gst_applied = gstFilter === 'true';

      const couriersPromise = couriersAPI.getAll().catch(() => ({ data: [] }));

      // Fetch orders with cache
      const cacheKey = `admin_shipping_${statusTab}_${paymentFilter}_${gstFilter}_${search}_${pageNum}_${limit}`;

      const ordersPromise = fetchWithCache(
        cacheKey,
        async () => {
          const res = await itemFilters.fetchOrders(params);
          return res.data;
        },
        (data, isFromCache) => {
          if (data && requestId === requestRef.current) {
            setOrders(data.orders || []);
            setTotalPages(data.pages || 1);
          }
        }
      );

      const [, couriersRes] = await Promise.all([ordersPromise, couriersPromise]);
      if (requestId === requestRef.current) setCouriers(couriersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      if (requestId === requestRef.current) { setOrders([]); toast.error('Failed to load filtered orders'); }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchData(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search, itemFilters.itemSearch, itemFilters.categories.category, itemFilters.categories.subcategory, itemFilters.categories.categoryIds, fetchData]);

  useEffect(() => {
    setPage(1);
    fetchData(1);
  }, [statusTab, paymentFilter, gstFilter, fetchData]);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setUpdateData({
      status: order.status,
      tracking_number: order.tracking_number || '',
      courier_provider: order.courier_provider || '',
      notes: '',
    });
    setShowDialog(true);
  };

  const handleShipOrder = async (order) => {
    if (!window.confirm(`Ship order #${order.order_number} via delivery partner?`)) return;
    
    setShippingOrder(order.id);
    try {
      // Use the courier API to ship the order directly
      const response = await courierAPI.shipOrder(order.id);
      
      if (response.data.success) {
        toast.success(`Order shipped successfully! AWB: ${response.data.awb}`);
        fetchData(); // Refresh the orders list
      } else {
        toast.error(response.data.error || 'Failed to ship order');
      }
    } catch (error) {
      console.error('Shipping error:', error);
      toast.error(error.response?.data?.detail || 'Failed to ship order');
    } finally {
      setShippingOrder(false);
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await ordersAPI.updateStatus(selectedOrder.id, updateData);
      toast.success('Order updated successfully');
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleBulkTrackUpdate = async () => {
    if (!window.confirm('Update all shipped orders with latest tracking status?')) return;
    
    setRefreshing(true);
    try {
      const response = await courierAPI.bulkTrackUpdate();
      toast.success(`${response.data.message} (${response.data.updated_count} orders updated)`);
      fetchData(); // Refresh the orders list
    } catch (error) {
      console.error('Bulk tracking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update tracking status');
    } finally {
      setRefreshing(false);
    }
  };

  const handlePrintInvoice = (order) => {
    // Support being called with order object or just order id (legacy)
    if (typeof order === 'string') {
      const found = orders.find(o => o.id === order);
      if (found) { setPrintOrder(found); setShowPrintDialog(true); }
      return;
    }
    setPrintOrder(order);
    setShowPrintDialog(true);
  };

  const executePrint = () => {
    if (!printOrder) return;
    generatePrintInvoice(printOrder, printSize);
    setShowPrintDialog(false);
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: 'bg-amber-500/20 text-amber-400',
      ready_to_ship: 'bg-blue-500/20 text-blue-400',
      shipped: 'bg-indigo-500/20 text-indigo-400',
      delivered: 'bg-emerald-500/20 text-emerald-400',
      returned: 'bg-red-500/20 text-red-400',
      cancelled: 'bg-slate-500/20 text-slate-400',
    };
    return classes[status] || 'bg-slate-500/20 text-slate-400';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      ready_to_ship: 'Ready to Ship',
      shipped: 'Shipped',
      delivered: 'Delivered',
      returned: 'Returned',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  const renderActionButtons = (order) => {
    const baseButtons = [
      <Button key="view" size="icon" variant="ghost" onClick={() => handleViewOrder(order)} title="View Order Details">
        <Eye className="w-4 h-4" />
      </Button>,
      <Button key="invoice" size="icon" variant="ghost" onClick={() => handlePrintInvoice(order)} title="Print Invoice">
        <FileText className="w-4 h-4" />
      </Button>
    ];

    switch (order.status) {
      case 'pending':
        return [
          ...baseButtons,
          <Button
            key="generate-awb"
            size="icon"
            variant="ghost"
            onClick={() => handleShipOrder(order)}
            disabled={shippingOrder === order.id}
            title="Generate AWB & Prepare for Shipping"
            className="text-blue-400 hover:text-blue-300 hover:bg-blue-400/10"
          >
            {shippingOrder === order.id ? (
              <RefreshCcw className="w-4 h-4 animate-spin" />
            ) : (
              <Package className="w-4 h-4" />
            )}
          </Button>,
          <Button
            key="reject"
            size="icon"
            variant="ghost"
            onClick={() => {
              setSelectedOrder(order);
              setUpdateData({ 
                status: 'cancelled', 
                tracking_number: '', 
                courier_provider: '', 
                notes: 'Order rejected - cannot fulfill' 
              });
              setShowDialog(true);
            }}
            title="Reject Order"
            className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
          >
            <X className="w-4 h-4" />
          </Button>
        ];

      case 'ready_to_ship':
        return [
          ...baseButtons,
          <Button
            key="ship"
            size="icon"
            variant="ghost"
            onClick={() => {
              setSelectedOrder(order);
              setUpdateData({ 
                status: 'shipped', 
                tracking_number: order.tracking_number || '', 
                courier_provider: order.courier_provider || 'Delhivery', 
                notes: 'Order shipped to customer' 
              });
              setShowDialog(true);
            }}
            title="Mark as Shipped"
            className="text-green-400 hover:text-green-300 hover:bg-green-400/10"
          >
            <Ship className="w-4 h-4" />
          </Button>,
          <Button
            key="cancel-shipment"
            size="icon"
            variant="ghost"
            onClick={async () => {
              if (!window.confirm(`Cancel shipment for order #${order.order_number}?`)) return;
              try {
                await courierAPI.cancelShipment(order.id);
                toast.success('Shipment cancelled');
                fetchData();
              } catch (error) {
                toast.error('Failed to cancel shipment');
              }
            }}
            title="Cancel Shipment"
            className="text-orange-400 hover:text-orange-300 hover:bg-orange-400/10"
          >
            <X className="w-4 h-4" />
          </Button>
        ];

      case 'shipped':
        return [
          ...baseButtons,
          <Button
            key="track"
            size="icon"
            variant="ghost"
            onClick={async () => {
              try {
                const response = await courierAPI.trackOrder(order.id);
                if (response.data.order_status_updated) {
                  toast.success(`Order status updated to: ${response.data.new_order_status}`);
                  fetchData();
                } else {
                  toast.info(`Current status: ${response.data.status}`);
                }
              } catch (error) {
                toast.error('Failed to track order');
              }
            }}
            title="Track & Update Status"
            className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-400/10"
          >
            <Truck className="w-4 h-4" />
          </Button>,
          <Button
            key="mark-delivered"
            size="icon"
            variant="ghost"
            onClick={() => {
              setSelectedOrder(order);
              setUpdateData({ 
                status: 'delivered', 
                tracking_number: order.tracking_number || '', 
                courier_provider: order.courier_provider || '', 
                notes: 'Order delivered to customer' 
              });
              setShowDialog(true);
            }}
            title="Mark as Delivered"
            className="text-green-400 hover:text-green-300 hover:bg-green-400/10"
          >
            <CheckCircle className="w-4 h-4" />
          </Button>
        ];

      case 'delivered':
        return [
          ...baseButtons,
          <Button
            key="delivered"
            size="icon"
            variant="ghost"
            title="Delivered"
            className="text-green-400 cursor-default"
          >
            <CheckCircle className="w-4 h-4" />
          </Button>
        ];

      case 'returned':
        return [
          ...baseButtons,
          <Button
            key="returned"
            size="icon"
            variant="ghost"
            title="Returned"
            className="text-red-400 cursor-default"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
          </Button>
        ];

      case 'cancelled':
        return [
          ...baseButtons,
          <Button
            key="cancelled"
            size="icon"
            variant="ghost"
            title="Cancelled"
            className="text-gray-400 cursor-default"
          >
            <X className="w-4 h-4" />
          </Button>
        ];

      default:
        return baseButtons;
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-shipping">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Online Sales (Customer Orders)</h1>
          <p className="text-slate-400">{orders.length} orders in {getStatusLabel(statusTab).toLowerCase()}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              setRefreshing(true);
              await fetchData();
              setRefreshing(false);
            }}
            className="border-slate-600 hover:bg-slate-700"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleBulkTrackUpdate}
            disabled={refreshing}
            className="border-slate-600 hover:bg-slate-700 text-blue-400"
          >
            <Truck className="w-4 h-4 mr-2" />
            Update Tracking
          </Button>
        </div>
      </div>

      {/* Status Tabs */}
      <Tabs value={statusTab} onValueChange={setStatusTab}>
        <TabsList className="bg-slate-800">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pending Orders
          </TabsTrigger>
          <TabsTrigger value="ready_to_ship" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Ready to Ship
          </TabsTrigger>
          <TabsTrigger value="shipped" className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            Shipped Orders
          </TabsTrigger>
          <TabsTrigger value="delivered" className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Delivered
          </TabsTrigger>
          <TabsTrigger value="returned" className="flex items-center gap-2">
            <ArrowRight className="w-4 h-4 rotate-180" />
            Returned
          </TabsTrigger>
          <TabsTrigger value="cancelled" className="flex items-center gap-2">
            <X className="w-4 h-4" />
            Cancelled
          </TabsTrigger>
        </TabsList>

        <TabsContent value={statusTab} className="space-y-4">
          <OrderItemFilters filters={itemFilters} onClear={() => { setSearch(''); setPaymentFilter('all'); setGstFilter('all'); }} />
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
        {/* Payment Filter */}
        <Select value={paymentFilter} onValueChange={setPaymentFilter}>
          <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700">
            <SelectValue placeholder="Payment Mode" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all">All Payment Modes</SelectItem>
            <SelectItem value="cod">Cash on Delivery</SelectItem>
            <SelectItem value="upi">UPI</SelectItem>
            <SelectItem value="card">Card</SelectItem>
            <SelectItem value="online">Online</SelectItem>
          </SelectContent>
        </Select>

        {/* GST Filter */}
        <Select value={gstFilter} onValueChange={setGstFilter}>
          <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700">
            <SelectValue placeholder="GST Status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">GST Applied</SelectItem>
            <SelectItem value="false">No GST</SelectItem>
          </SelectContent>
        </Select>

            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by order #, phone, or customer name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 input-admin"
              />
            </div>
          </div>

          {/* Orders Table */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Order #</TableHead>
                    <TableHead className="text-slate-400">Date</TableHead>
                    <TableHead className="text-slate-400">Customer</TableHead>
                    <TableHead className="text-slate-400">Items</TableHead>
                    <TableHead className="text-slate-400">Total</TableHead>
                    <TableHead className="text-slate-400">Payment</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
                    </TableRow>
                  ) : orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                        <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        No {getStatusLabel(statusTab).toLowerCase()} orders found
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => (
                      <TableRow key={order.id} className="border-slate-700">
                        <TableCell className="font-mono font-medium">{order.order_number}</TableCell>
                        <TableCell className="text-slate-400">
                          {new Date(order.created_at).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell>{order.customer_name || order.customer_phone || '-'}</TableCell>
                        <TableCell>{order.items.length} items</TableCell>
                        <TableCell className="font-semibold">₹{order.grand_total.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize border-slate-600">
                            {order.payment_method}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeClass(order.status)}>
                            {getStatusLabel(order.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {renderActionButtons(order)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newPage = Math.max(1, page - 1);
                  setPage(newPage);
                  fetchData(newPage);
                }}
                disabled={page <= 1}
                className="border-slate-700 hover:bg-slate-800"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newPage = Math.min(totalPages, page + 1);
                  setPage(newPage);
                  fetchData(newPage);
                }}
                disabled={page >= totalPages}
                className="border-slate-700 hover:bg-slate-800"
              >
                Next
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Order Detail Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle>Order #{selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Items */}
              <div>
                <h4 className="font-semibold mb-3">Items</h4>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="flex justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.product_name || 'Unknown Product'}</p>
                        <p className="text-sm text-slate-400">
                          SKU: {item.sku || 'N/A'} | Qty: {item.quantity || 0} | Unit: ₹{(item.price || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">₹{(item.total || (item.price * item.quantity) || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-700 flex justify-between font-bold">
                  <span>Total</span>
                  <span>₹{selectedOrder.grand_total.toLocaleString()}</span>
                </div>
              </div>

              {/* Shipping Address */}
              <div>
                <h4 className="font-semibold mb-3">Shipping Address</h4>
                <div className="p-3 bg-slate-700/50 rounded-lg text-sm">
                  <p className="font-medium">{selectedOrder.shipping_address?.name || 'N/A'}</p>
                  <p className="text-slate-400">{selectedOrder.shipping_address?.line1 || ''}</p>
                  {selectedOrder.shipping_address?.line2 && <p className="text-slate-400">{selectedOrder.shipping_address.line2}</p>}
                  <p className="text-slate-400">
                    {selectedOrder.shipping_address?.city || ''}, {selectedOrder.shipping_address?.state || ''} - {selectedOrder.shipping_address?.pincode || ''}
                  </p>
                  <p className="text-slate-400">Phone: {selectedOrder.shipping_address?.phone || selectedOrder.customer_phone || 'N/A'}</p>
                </div>
              </div>

              {/* Update Status */}
              <div className="space-y-4 pt-4 border-t border-slate-700">
                <h4 className="font-semibold">Update Order Status</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={updateData.status} onValueChange={(v) => setUpdateData({ ...updateData, status: v })}>
                      <SelectTrigger className="input-admin">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="ready_to_ship">Ready to Ship</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Courier Provider</Label>
                    <Select value={updateData.courier_provider} onValueChange={(v) => setUpdateData({ ...updateData, courier_provider: v })}>
                      <SelectTrigger className="input-admin">
                        <SelectValue placeholder="Select courier" />
                      </SelectTrigger>
                      <SelectContent>
                        {couriers.map((c) => (
                          <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tracking Number</Label>
                  <Input
                    value={updateData.tracking_number}
                    onChange={(e) => setUpdateData({ ...updateData, tracking_number: e.target.value })}
                    placeholder="Enter tracking number"
                    className="input-admin"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={updateData.notes}
                    onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                    placeholder="Add a note..."
                    className="input-admin"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
                  <Button onClick={handleUpdateStatus} className="bg-primary hover:bg-primary/90">
                    Update Order
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Size Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Choose Print Size
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Label className="text-sm text-slate-300">Select print format</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setPrintSize('a4')}
                className={`p-4 rounded-lg border-2 text-left transition-colors ${printSize === 'a4' ? 'border-primary bg-primary/10' : 'border-slate-600 hover:border-slate-500'}`}
              >
                <FileText className="w-6 h-6 mb-2 text-primary" />
                <p className="font-medium">A4 Size</p>
                <p className="text-xs text-slate-400 mt-1">Full invoice with product images</p>
              </button>
              <button
                onClick={() => setPrintSize('thermal')}
                className={`p-4 rounded-lg border-2 text-left transition-colors ${printSize === 'thermal' ? 'border-primary bg-primary/10' : 'border-slate-600 hover:border-slate-500'}`}
              >
                <Receipt className="w-6 h-6 mb-2 text-primary" />
                <p className="font-medium">Thermal</p>
                <p className="text-xs text-slate-400 mt-1">Compact 58mm/80mm format</p>
              </button>
            </div>
            <div className="flex gap-2 pt-2">
              <Button className="flex-1" onClick={executePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print Now
              </Button>
              <Button variant="outline" onClick={() => setShowPrintDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
